import { useState } from "react";
import { Search, Trash2, Pencil, Filter, Upload } from "lucide-react";
import AdminLayout from "../../layouts/AdminLayout";

// ── constants ──────────────────────────────────────────────────────────────────

const DUMMY_BOXES = [
  {
    id: 1,
    title: "CVE-2017-0144 — EternalBlue 2.0",
    category: "CSF-05: Password Cracking 101",
    framework: "MITRE ATT&CK",
    topology: "LAN Segment",
    audience: "students",
    status: "active",
  },
  {
    id: 2,
    title: "CVE-2021-44228 — Log4Shell Web Exploit",
    category: "NDF-02: IDS Signature Writing",
    framework: "OWASP",
    topology: "DMZ",
    audience: "students",
    status: "active",
  },
  {
    id: 3,
    title: "CVE-2020-1472 — Zerologon Lab",
    category: "CTH-02: MITRE ATT&CK Framework",
    framework: "MITRE ATT&CK",
    topology: "Enterprise",
    audience: "professionals",
    status: "maintenance",
  },
  {
    id: 4,
    title: "CVE-2022-0778 — Crypto Challenge",
    category: "BCY-01: Symmetric vs. Asymmetric Encryption",
    framework: "Custom",
    topology: "Isolated",
    audience: "students",
    status: "draft",
  },
  {
    id: 5,
    title: "CVE-2021-3156 — Privilege Escalation",
    category: "BSP-04: Buffer Overflow Attack",
    framework: "MITRE ATT&CK",
    topology: "LAN Segment",
    audience: "professionals",
    status: "active",
  },
];

const STATUS_CONFIG = {
  active: { label: "Active", dot: "bg-emerald-400", text: "text-emerald-400" },
  maintenance: {
    label: "Maintenance",
    dot: "bg-amber-400",
    text: "text-amber-400",
  },
  draft: { label: "Draft", dot: "bg-slate-400", text: "text-slate-400" },
};

// ── Main page ──────────────────────────────────────────────────────────────────

export default function BoxManagement() {
  const [boxes, setBoxes] = useState(DUMMY_BOXES);
  const [search, setSearch] = useState("");
  const [filterAudience, setFilterAudience] = useState("all");
  // const [deleteTarget, setDeleteTarget] = useState(null);

  const filtered = boxes.filter((b) => {
    const q = search.toLowerCase();
    const matchQ =
      !q ||
      b.title.toLowerCase().includes(q) ||
      (b.category || "").toLowerCase().includes(q);
    return (
      matchQ && (filterAudience === "all" || b.audience === filterAudience)
    );
  });

  return (
    <AdminLayout>
      <div className="px-8 py-8 space-y-6 min-h-screen">
        {/* Page header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-(--color-text-primary)">
              Box Management
            </h1>
            <p className="text-xs mt-0.5 text-slate-400">
              Manage, monitor, and deploy cybersecurity training modules.
            </p>
          </div>

          {/* Upload Box — gray/disabled-style button */}
          <button
            disabled
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium bg-slate-200 text-slate-500 border border-slate-300 cursor-not-allowed shrink-0 whitespace-nowrap dark:bg-slate-700 dark:text-slate-400 dark:border-slate-600"
          >
            <Upload size={16} />
            Upload Box
          </button>
        </div>

        {/* Table */}
        {/* <div className="rounded-xl bg-(--color-bg-card) border border-(--color-border)">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-6 py-4 border-b border-(--color-border)">
            <div className="relative w-full sm:w-72">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by title or category…"
                className="w-full pl-9 pr-4 py-2 rounded-lg border border-(--color-border) bg-(--color-bg) text-sm text-(--color-text-primary) placeholder:text-slate-400 focus:outline-none focus:border-(--color-primary) focus:shadow-[0_0_0_3px_var(--color-primary-100)] transition-all"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter size={13} className="text-slate-400" />
              {["all", "students", "professionals"].map((v) => (
                <button
                  key={v}
                  onClick={() => setFilterAudience(v)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                    filterAudience === v
                      ? "bg-(--color-text-primary) text-(--color-bg-card)"
                      : "bg-(--color-bg) border border-(--color-border) text-slate-400 hover:text-(--color-text-primary)"
                  }`}
                >
                  {v === "all" ? "All" : v}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-(--color-border)">
                  {[
                    "CVE / Title",
                    "Category",
                    "Framework",
                    "Topology",
                    "Audience",
                    "Status",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-6 py-3.5 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-(--color-border)">
                {filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-14 text-center text-sm text-slate-400"
                    >
                      No boxes found.
                    </td>
                  </tr>
                ) : (
                  filtered.map((box) => {
                    const st = STATUS_CONFIG[box.status] ?? STATUS_CONFIG.draft;
                    return (
                      <tr
                        key={box.id}
                        className="hover:bg-(--color-bg)/50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <p className="font-medium text-(--color-text-primary) truncate max-w-[220px]">
                            {box.title}
                          </p>
                        </td>
                        <td className="px-6 py-4 max-w-[180px]">
                          <span className="text-xs text-(--color-text-primary) truncate block">
                            {box.category}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-(--color-text-primary) whitespace-nowrap">
                          {box.framework}
                        </td>
                        <td className="px-6 py-4 text-(--color-text-primary) whitespace-nowrap">
                          {box.topology}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${
                              box.audience === "students"
                                ? "bg-violet-500/10 text-violet-400"
                                : "bg-sky-500/10 text-sky-400"
                            }`}
                          >
                            {box.audience}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`flex items-center gap-1.5 text-xs font-medium ${st.text}`}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${st.dot}`}
                            />
                            {st.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <button
                              disabled
                              className="p-1.5 rounded-lg text-slate-400 hover:text-(--color-primary) hover:bg-(--color-primary)/10 transition-colors"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              disabled
                              // onClick={() => setDeleteTarget(box)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-3.5 border-t border-(--color-border)">
            <p className="text-xs text-slate-400">
              Showing{" "}
              <span className="font-medium text-(--color-text-primary)">
                {filtered.length}
              </span>{" "}
              of{" "}
              <span className="font-medium text-(--color-text-primary)">
                {boxes.length}
              </span>{" "}
              boxes
            </p>
          </div>
        </div> */}
      </div>

      {/* Delete confirmation dialog
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div
            className="w-full max-w-sm mx-4 rounded-2xl border border-(--color-border) shadow-2xl p-6 space-y-4"
            style={{ backgroundColor: "var(--color-bg-card)" }}
          >
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-full bg-red-500/10">
                <Trash2 size={18} className="text-red-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-(--color-text-primary)">
                  Delete Box
                </h3>
                <p className="text-xs text-slate-400">
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <p className="text-sm text-slate-400">
              Delete{" "}
              <span className="font-semibold text-(--color-text-primary)">
                {deleteTarget.title}
              </span>
              ?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded-lg text-sm font-medium border border-(--color-border) text-(--color-text-primary) hover:bg-(--color-bg) transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setBoxes((p) => p.filter((b) => b.id !== deleteTarget.id));
                  setDeleteTarget(null);
                }}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )} */}
    </AdminLayout>
  );
}
